import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, InputNumber, Popover, Radio, Slider } from 'antd';
import { Divider, SearchInput } from 'common/components';
import { ArrowDownShort, ArrowUpShort, CloseGreen } from 'common/svg';
import { EPresetType } from 'common/const';
import { DEFAULT_VALUE_0, ITEMS_FOR_VIEW_LIMIT_10, PRICE_LIMIT } from 'common/config';
import { replaceCommaWithPeriod, stringToNumber } from 'common/helpers';
import { IProperty } from 'entities/Property/Property.models';
import {
  filterOptionsBySearch,
  getPropertyPresetType,
  getRangePropertyMinMaxValue,
  mapPropertyValuesToOptions,
} from 'entities/Property/Property.helper';
import { IPresetRange } from 'entities/Presets/Preset.models';

interface IComponentProps {
  property: IProperty;
  currentValues: string[];
  currentRange?: IPresetRange;
  onApply: (id: number, value: string[]) => void;
}

export const CatalogGoodsListFiltersProperty: FC<IComponentProps> = ({ property, currentValues, currentRange, onApply }) => {
  const [search, setSearch] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string[]>([]);

  const { isRange } = getPropertyPresetType(property);
  const { min, max } = getRangePropertyMinMaxValue(property.values);

  useEffect(() => {
    if (open) {
      if (isRange) {
        if (currentRange) {
          setValue([currentRange?.from?.toString() ?? '', currentRange?.to?.toString() ?? '']);
        } else {
          setValue([min, max]);
        }
      } else {
        setValue(currentValues);
      }
    }
  }, [open]);

  const options = useMemo(() => {
    return mapPropertyValuesToOptions(property.values);
  }, [property.values]);

  const filteredOptions = useMemo(() => {
    return filterOptionsBySearch(options, search);
  }, [options, search]);
  const displayName = useMemo(() => {
    let displayName = property.displayName;

    if (property.unitOfMeasurement) {
      displayName += `, ${property.unitOfMeasurement}`;
    }

    if (currentValues.length && !isRange) {
      return `${displayName}: ${currentValues.length}`;
    }

    return displayName;
  }, [property, currentValues]);

  const onApplyClick = () => {
    if (isRange && value.every((item) => !item.length)) {
      onApply(property.id, []);
    } else {
      onApply(property.id, value);
    }

    setOpen(false);
  };

  const onClearClick = () => {
    onApply(property.id, []);
  };

  const renderPopoverContent = () => {
    switch (property.presetType) {
      case EPresetType.SingleValue: {
        return (
          <>
            {options.length > ITEMS_FOR_VIEW_LIMIT_10 && (
              <div className="mb-6">
                <SearchInput
                  auto
                  placeholder="Поиск"
                  minLength={DEFAULT_VALUE_0}
                  onSearch={async (value) => setSearch(value || '')}
                />
              </div>
            )}

            <div className="mb-4 overflow-y-scroll scrollbar" style={{ minWidth: 262, maxHeight: 270 }}>
              <Radio.Group
                className="catalog-goods-list-filters-property__single"
                options={filteredOptions}
                value={value[0]}
                onChange={(e) => setValue([e.target.value])}
              />
            </div>
          </>
        );
      }
      case EPresetType.MultipleValue:
      case EPresetType.Checkbox: {
        return (
          <>
            {options.length > ITEMS_FOR_VIEW_LIMIT_10 && (
              <div className="mb-6">
                <SearchInput
                  auto
                  placeholder="Поиск"
                  minLength={DEFAULT_VALUE_0}
                  onSearch={async (value) => setSearch(value ?? '')}
                />
              </div>
            )}

            <div className="mb-4 overflow-y-scroll scrollbar" style={{ minWidth: 262, maxHeight: 270 }}>
              <Checkbox.Group
                className="catalog-goods-list-filters-property__multiple"
                options={filteredOptions}
                value={value}
                onChange={(value) => setValue(value as string[])}
              />
            </div>
          </>
        );
      }
      case EPresetType.Range: {
        return (
          <div className="mb-4" style={{ minWidth: 400 }}>
            <div className="flex align-center" style={{ gap: 12 }}>
              <InputNumber
                prefix="от"
                controls={false}
                step="0.1"
                placeholder={min}
                min={min.length ? min : DEFAULT_VALUE_0}
                max={value[1] ? value[1] : max}
                parser={replaceCommaWithPeriod}
                formatter={replaceCommaWithPeriod}
                value={value[0]}
                onChange={(value) => {
                  setValue((prev) => [value === null ? '' : value.toString(), prev[1]]);
                }}
              />
              <InputNumber
                prefix="до"
                controls={false}
                step="0.1"
                placeholder={max}
                min={value[0] ? value[0] : min}
                max={max ? max : PRICE_LIMIT}
                parser={replaceCommaWithPeriod}
                formatter={replaceCommaWithPeriod}
                value={value[1]}
                onChange={(value) => {
                  setValue((prev) => [prev[0], value === null ? '' : value.toString()]);
                }}
              />
            </div>

            {min !== max && (
              <div className="m-5">
                <Slider
                  range
                  min={stringToNumber(min)}
                  max={stringToNumber(max)}
                  step={0.1}
                  value={value.map((item) => stringToNumber(item))}
                  onChange={(value) => setValue(value.map((item) => item.toString()))}
                />
              </div>
            )}
          </div>
        );
      }
      default: {
        return null;
      }
    }
  };

  return currentValues.length || currentRange ? (
    <div className="catalog-goods-list-filters-property with-value">
      {displayName}

      <Button className="button-icon" icon={<CloseGreen className="icon-close-green" />} onClick={onClearClick} />
    </div>
  ) : (
    <Popover
      rootClassName="redesign catalog-goods-list-filters-property__popover"
      trigger="click"
      placement="bottomLeft"
      open={open}
      onOpenChange={setOpen}
      content={
        <div>
          {renderPopoverContent()}

          <Divider marginY={20} />

          <Button className="w-full button-sm primary" onClick={onApplyClick}>
            Применить
          </Button>
        </div>
      }
    >
      <div className={`catalog-goods-list-filters-property ${open ? 'opened' : ''}`}>
        {displayName}

        {open ? <ArrowUpShort /> : <ArrowDownShort />}
      </div>
    </Popover>
  );
};
